import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/axiosConfig';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Loader2, ArrowLeft } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export default function AttributeAnalyticsPage() {
  const { t } = useLanguage();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState([]);
  const [visible, setVisible] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/training/periodization/analytics/attribute-series', {
        params: { from: from || undefined, to: to || undefined },
      });
      const s = res.data.series || [];
      setSeries(s);
      const vis = {};
      s.forEach((row) => {
        vis[`${row.block_id}-${row.type_key}`] = true;
      });
      setVisible(vis);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, []);

  const chartGroups = useMemo(() => {
    const byType = {};
    for (const row of series) {
      if (!byType[row.type_key]) byType[row.type_key] = [];
      byType[row.type_key].push(row);
    }
    return byType;
  }, [series]);

  return (
    <div className='mx-auto max-w-5xl space-y-6 p-4 md:p-6'>
      <div className='flex flex-wrap items-center gap-3'>
        <Button variant='ghost' size='sm' asChild>
          <Link to='/training-plan/design'>
            <ArrowLeft className='mr-1 h-4 w-4' />
            {t('periodization.designTitle')}
          </Link>
        </Button>
        <h1 className='text-xl font-semibold'>{t('periodization.analytics.title')}</h1>
      </div>
      <p className='text-sm text-muted-foreground'>{t('periodization.analytics.subtitle')}</p>

      <div className='flex flex-wrap gap-4 items-end'>
        <div>
          <Label className='text-xs'>{t('periodization.analytics.from')}</Label>
          <Input type='date' className='mt-1' value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label className='text-xs'>{t('periodization.analytics.to')}</Label>
          <Input type='date' className='mt-1' value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Button type='button' onClick={load} disabled={loading}>
          {loading ? <Loader2 className='h-4 w-4 animate-spin' /> : t('periodization.analytics.refresh')}
        </Button>
      </div>

      {Object.entries(chartGroups).map(([typeKey, rows]) => {
        const weekMap = new Map();
        for (const row of rows) {
          if (!visible[`${row.block_id}-${row.type_key}`]) continue;
          for (const pt of row.points || []) {
            const key = pt.week_start || pt.week_number;
            if (!weekMap.has(key)) weekMap.set(key, { label: `W${pt.week_number}`, week: pt.week_number });
            weekMap.get(key)[`${row.block_name}`] = pt.numeric_value;
          }
        }
        const data = [...weekMap.values()].sort((a, b) => a.week - b.week);
        if (!data.length) return null;
        return (
          <Card key={typeKey}>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm'>{rows[0]?.type_name || typeKey}</CardTitle>
            </CardHeader>
            <CardContent className='min-h-[220px]'>
              <div className='flex flex-wrap gap-2 mb-2'>
                {rows.map((row) => {
                  const id = `${row.block_id}-${row.type_key}`;
                  return (
                    <button
                      key={id}
                      type='button'
                      className={`text-xs px-2 py-1 rounded border ${visible[id] ? 'bg-accent' : 'opacity-40'}`}
                      onClick={() => setVisible((v) => ({ ...v, [id]: !v[id] }))}
                    >
                      {row.block_name}
                    </button>
                  );
                })}
              </div>
              <ResponsiveContainer width='100%' height={200}>
                <LineChart data={data}>
                  <XAxis dataKey='label' tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend />
                  {rows
                    .filter((row) => visible[`${row.block_id}-${row.type_key}`])
                    .map((row, i) => (
                      <Line
                        key={row.block_id}
                        type='monotone'
                        dataKey={row.block_name}
                        stroke={['hsl(var(--primary))', '#22c55e', '#ef4444'][i % 3]}
                        dot={{ r: 2 }}
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
